/**
 * #/group/_all — Devices tab bulk fleet (filter, select, group/zone apply).
 */

import { fleetVisibleTotalFrom } from "./fleet-list-paginated.js";

/**
 * @returns {string}
 */
export function buildGroupAllDevicesBulkToolbarHtml() {
  return `
    <div class="grp-all-devices-bulk glass-panel glass-panel--inset">
      <div class="inline-form grp-all-devices-bulk__filter">
        <label class="field" style="max-width:min(100%, 360px)">
          <span>Filter</span>
          <input type="search" id="grpAllDevFilter" placeholder="id / name / group / zone (min 2 for server search)" autocomplete="off" />
        </label>
      </div>
      <div class="action-bar grp-all-devices-bulk__actions">
        <span class="chip" id="grpBulkSelStat">0 selected · 0 visible</span>
        <button class="btn sm secondary" id="grpBulkSelectVisible" type="button">Select visible</button>
        <button class="btn sm secondary" id="grpBulkClearSel" type="button" disabled>Clear selection</button>
        <details class="toolbar-collapse grp-all-devices-bulk__details">
          <summary>Selected boards</summary>
          <div class="inline-form" style="margin-top:2px">
            <label class="field">
              <span>Notification group</span>
              <input id="grpBulkGroupValue" maxlength="80" placeholder="empty = clear group" />
            </label>
            <button class="btn sm" id="grpBulkApplyGroup" type="button" disabled>Apply group</button>
            <label class="field">
              <span>Zone override</span>
              <input id="grpBulkZoneValue" maxlength="31" placeholder="e.g. all / Zone-A" />
            </label>
            <div class="row" style="gap:8px;justify-content:flex-end;flex-wrap:wrap">
              <button class="btn sm" id="grpBulkApplyZone" type="button" disabled>Apply zone</button>
              <button class="btn sm secondary" id="grpBulkClearZone" type="button" disabled>Clear zone override</button>
            </div>
          </div>
        </details>
      </div>
    </div>`;
}

/**
 * @param {ParentNode} mountEl
 */
export function mountGroupAllDevicesBulkToolbar(mountEl) {
  if (!mountEl) return false;
  if (mountEl.dataset.mounted === "1") return true;
  mountEl.innerHTML = buildGroupAllDevicesBulkToolbarHtml();
  mountEl.dataset.mounted = "1";
  mountEl.hidden = false;
  return true;
}

function fmtMacDisplay(d) {
  const disp = String((d && d.mac_display) || "").trim();
  if (disp) return disp;
  const raw = String((d && d.mac_nocolon) || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
  if (raw.length !== 12) return raw || "";
  return raw.match(/.{1,2}/g).join(":");
}

/**
 * @param {object} ctx
 * @returns {object}
 */
export function createGroupAllDevicesFleetPanel(ctx) {
  const {
    view,
    routeSeq,
    $,
    setChildMarkup,
    mountDeviceGridVirtualList,
    virtualMin,
    api,
    toast,
    escapeHtml,
    fmtRel,
    canonicalGroupKey,
    isRouteCurrent,
    bustDeviceListCaches,
    buildPostDevicesBulkProfilePath,
    buildDevicesFirmwareHintsPath,
    DEVICES_FIRMWARE_HINTS_TIMEOUT_MS,
    pickDevicesPollPath,
    FLEET_DEVICES_INITIAL_PAGES,
    debounceDevicesSearch,
    devicesSearchQueryReady,
    devicesSearchTooShort,
    devicesServerSearchActive,
    syncDevicesSearchMinLenBanner,
    syncDevicesServerSearchBanner,
    fetchFleetDevicesList,
    fetchFleetDevicesPage,
    devicePresenceStatus,
    devicePresenceBadgeText,
    getMe,
    onFleetPageChange,
  } = ctx;

  let allItems = [];
  let fleetNextCursor = null;
  let fleetHasMore = false;
  let fleetVisibleTotal = null;
  let activeServerSearchQ = "";
  const selectedIds = new Set();
  const hintById = new Map();
  let deviceGridVirtualDispose = null;

  const getRows = () => allItems;
  const getSearchQ = () => activeServerSearchQ;
  const getFleetCursor = () => fleetNextCursor;
  const getFleetHasMore = () => fleetHasMore;

  const filteredItems = () => {
    if (devicesServerSearchActive(activeServerSearchQ)) return allItems;
    const inp = $("#grpAllDevFilter", view);
    const q = inp ? String(inp.value || "").trim().toLowerCase() : "";
    return allItems.filter((d) => {
      if (!q) return true;
      const did = String(d.device_id || "").toLowerCase();
      const nm = String(d.display_label || "").toLowerCase();
      const grp = String(d.notification_group || "").toLowerCase();
      const zn = String(d.zone || "").toLowerCase();
      const note = String(d.grantee_display_note || "").toLowerCase();
      const ow = String(d.owner_admin || "").toLowerCase();
      const mac = fmtMacDisplay(d).toLowerCase();
      return did.includes(q) || nm.includes(q) || grp.includes(q) || zn.includes(q)
        || note.includes(q) || ow.includes(q) || mac.includes(q);
    });
  };

  const bulkBarState = () => {
    const c = selectedIds.size;
    const stat = $("#grpBulkSelStat", view);
    const grpBtn = $("#grpBulkApplyGroup", view);
    const zoBtn = $("#grpBulkApplyZone", view);
    const zcBtn = $("#grpBulkClearZone", view);
    const selVisBtn = $("#grpBulkSelectVisible", view);
    const clrBtn = $("#grpBulkClearSel", view);
    const totalVisible = filteredItems().length;
    if (stat) {
      stat.textContent = fleetVisibleTotal != null
        ? `${c} selected · ${totalVisible} on page · ${fleetVisibleTotal} fleet`
        : `${c} selected · ${totalVisible} visible`;
    }
    const disable = c === 0;
    if (grpBtn) grpBtn.disabled = disable;
    if (zoBtn) zoBtn.disabled = disable;
    if (zcBtn) zcBtn.disabled = disable;
    if (clrBtn) clrBtn.disabled = disable;
    if (selVisBtn) selVisBtn.disabled = totalVisible === 0;
  };

  const deviceListCard = (d) => {
    const pres = devicePresenceStatus(d);
    const badgeTxt = devicePresenceBadgeText(pres);
    const did = String(d.device_id || "");
    const checked = selectedIds.has(did);
    const me = typeof getMe === "function" ? getMe() : null;
    const myNote = d.is_shared && d.grantee_display_note && String(d.grantee_display_note).trim();
    const name = (d.display_label && String(d.display_label).trim()) || did || "unknown";
    const letter = escapeHtml(name.slice(0, 1).toUpperCase() || "?");
    const showOwnerTag = !!(d.owner_admin && me && (me.role === "superadmin" || d.is_shared));
    const zone = d.zone ? String(d.zone).trim() : "";
    const group = d.notification_group ? String(d.notification_group).trim() : "";
    const fw = d.fw != null && String(d.fw).trim() ? `v${String(d.fw).trim()}` : "";
    const needFw = !!(d.firmware_hint && d.firmware_hint.update_available && firmwareHintStillValid(d.fw, d.firmware_hint));
    const metaParts = [
      zone,
      group,
      fw,
      showOwnerTag ? String(d.owner_admin) : "",
      d.is_shared ? "shared" : "",
      `Updated ${fmtRel(d.updated_at)}`,
    ].filter(Boolean);
    const noteLine = myNote
      ? `<span class="croc-cc-zone__meta device-card__fleet-note" title="Your private label">Note · ${escapeHtml(String(myNote))}</span>`
      : "";
    const fwCta = needFw
      ? `<span class="device-card__fleet-fw"><span class="device-fw-pill" title="Newer build on server">Update</span><button type="button" class="btn sm secondary fw-hint-cta fw-hint-cta--sm js-fw-hint" data-did="${escapeHtml(did)}" title="View update details" aria-label="Firmware update">更新</button></span>`
      : "";
    return `<div class="device-card--fleet-row">` +
      `<label class="device-card__pick-wrap device-card__pick-wrap--inline" title="Select for bulk actions"><input type="checkbox" class="bulk-dev-pick" data-device-id="${escapeHtml(did)}" ${checked ? "checked" : ""} aria-label="Select ${escapeHtml(did)}" /></label>` +
      `<a class="device-card device-card--fleet-pick croc-cc-zone" href="#/devices/${encodeURIComponent(did)}">` +
      `<span class="croc-cc-zone__icon device-card__fleet-icon" aria-hidden="true">${letter}</span>` +
      `<span class="croc-cc-zone__body"><span class="grp-sidebar-card__row device-card__fleet-row">` +
      `<strong class="croc-cc-zone__name" title="${escapeHtml(did)}">${escapeHtml(name)}</strong>` +
      `<span class="badge ${pres.cls} grp-sidebar-card__badge">${escapeHtml(badgeTxt)}</span></span>` +
      `<span class="croc-cc-zone__meta">${escapeHtml(metaParts.join(" · "))}</span>${noteLine}${fwCta}` +
      `</span></a></div>`;
  };

  const render = () => {
    const items = filteredItems();
    const grid = $("#groupPageDevices", view);
    if (!grid) return;
    if (typeof deviceGridVirtualDispose === "function") {
      try { deviceGridVirtualDispose(); } catch (_) {}
      deviceGridVirtualDispose = null;
    }
    try {
      grid.classList.remove("device-grid--skeleton");
      grid.removeAttribute("aria-busy");
    } catch (_) {}
    if (allItems.length === 0) {
      setChildMarkup(grid, `<p class="muted" style="padding:8px 0">No boards in your scope.</p>`);
      bulkBarState();
      return;
    }
    if (items.length === 0) {
      setChildMarkup(grid, `<p class="muted" style="padding:8px 0">No matches.</p>`);
      bulkBarState();
      return;
    }
    const htmlRows = items.map((d) => deviceListCard(d));
    if (htmlRows.length < virtualMin) {
      setChildMarkup(grid, htmlRows.join(""));
    } else {
      grid.innerHTML = "";
      deviceGridVirtualDispose = mountDeviceGridVirtualList(grid, htmlRows);
    }
    bulkBarState();
  };

  const mergeFirmwareHintsObject = (raw) => {
    const o = raw && typeof raw === "object" ? raw : {};
    hintById.clear();
    for (const k of Object.keys(o)) hintById.set(k, o[k]);
    for (const it of allItems) {
      const k = String(it.device_id);
      const h = o[k];
      it.firmware_hint = h && h.update_available && firmwareHintStillValid(it.fw, h) ? h : null;
    }
  };

  const notifyFleetChange = () => {
    if (typeof onFleetPageChange === "function") {
      onFleetPageChange({
        items: allItems,
        nextCursor: fleetNextCursor,
        hasMore: fleetHasMore,
      });
    }
  };

  const hydrate = ({ items, page, hasMore, nextCursor }) => {
    allItems = Array.isArray(items) ? items : [];
    fleetNextCursor = nextCursor != null ? String(nextCursor) : (
      page && page.next_cursor != null ? String(page.next_cursor) : null
    );
    fleetHasMore = hasMore != null ? !!hasMore : !!(page && page.has_more);
    const vt = fleetVisibleTotalFrom(page);
    if (vt != null) fleetVisibleTotal = vt;
    for (const did of Array.from(selectedIds)) {
      if (!allItems.some((x) => String(x.device_id) === did)) selectedIds.delete(did);
    }
    render();
    notifyFleetChange();
  };

  const runBulkProfile = async (payload) => {
    if (!selectedIds.size) { toast("Select at least one board", "err"); return; }
    const ids = Array.from(selectedIds.values());
    const r = await api(buildPostDevicesBulkProfilePath(), {
      method: "POST",
      body: Object.assign({ device_ids: ids }, payload || {}),
    });
    bustDeviceListCaches();
    toast(`Selected boards · ${Number(r.count || ids.length)} updated`, "ok");
    await loadDevicesAndHints();
  };

  const loadDevicesAndHints = async (opts = {}) => {
    if (!isRouteCurrent(routeSeq)) return;
    const append = !!(opts && opts.append);
    const searchQ = opts && opts.searchQ != null ? String(opts.searchQ).trim() : activeServerSearchQ;
    const apiSearchQ = devicesSearchQueryReady(searchQ) ? searchQ : "";
    activeServerSearchQ = apiSearchQ;
    let r;
    try {
      const devPath = pickDevicesPollPath();
      const live = devPath.includes("skip_cache=1");
      if (append && fleetNextCursor) {
        const pg = await fetchFleetDevicesPage({
          live,
          cursor: fleetNextCursor,
          searchQ: apiSearchQ || null,
          timeoutMs: 20000,
          retries: 2,
        });
        const seen = new Set(allItems.map((d) => String(d.device_id || "")));
        const extra = (pg.items || []).filter((d) => {
          const id = String(d.device_id || "");
          return id && !seen.has(id);
        });
        r = {
          items: allItems.concat(extra),
          page: pg.page,
          truncated: !!(pg.page && pg.page.has_more),
        };
      } else {
        r = await fetchFleetDevicesList({
          live,
          timeoutMs: 20000,
          retries: 2,
          maxPages: opts.maxPages != null ? opts.maxPages : FLEET_DEVICES_INITIAL_PAGES,
          searchQ: apiSearchQ || null,
        });
      }
    } catch (e) {
      if (isRouteCurrent(routeSeq)) toast(String(e && e.message) || "Failed to load boards", "err");
      return;
    }
    if (!isRouteCurrent(routeSeq)) return;
    allItems = Array.isArray(r.items) ? r.items : [];
    fleetNextCursor = r.page && r.page.next_cursor != null ? String(r.page.next_cursor) : null;
    fleetHasMore = !!(r.page && r.page.has_more);
    const vt = fleetVisibleTotalFrom(r);
    if (vt != null) fleetVisibleTotal = vt;
    syncDevicesServerSearchBanner(view, devicesServerSearchActive(apiSearchQ));
    syncDevicesSearchMinLenBanner(view, devicesSearchTooShort(searchQ));
    for (const did of Array.from(selectedIds)) {
      if (!allItems.some((x) => String(x.device_id) === did)) selectedIds.delete(did);
    }
    for (const it of allItems) {
      it.firmware_hint = null;
    }
    render();
    notifyFleetChange();
    try {
      const hintRes = await api(buildDevicesFirmwareHintsPath(), {
        timeoutMs: DEVICES_FIRMWARE_HINTS_TIMEOUT_MS,
        retries: 0,
      });
      if (!isRouteCurrent(routeSeq)) return;
      mergeFirmwareHintsObject((hintRes && hintRes.hints) || {});
    } catch (_) {}
    if (isRouteCurrent(routeSeq)) render();
  };

  const loadFirmwareHints = async () => {
    if (!isRouteCurrent(routeSeq) || !allItems.length) return;
    try {
      const hintRes = await api(buildDevicesFirmwareHintsPath(), {
        timeoutMs: DEVICES_FIRMWARE_HINTS_TIMEOUT_MS,
        retries: 0,
      });
      if (!isRouteCurrent(routeSeq)) return;
      mergeFirmwareHintsObject((hintRes && hintRes.hints) || {});
    } catch (_) {}
    if (isRouteCurrent(routeSeq)) render();
  };

  let gridWired = false;
  let bulkWired = false;
  const wireGrid = () => {
    if (gridWired) return;
    gridWired = true;
    const grid = $("#groupPageDevices", view);
    if (grid) {
      grid.addEventListener("change", (ev) => {
        const el = ev.target;
        if (!(el instanceof HTMLInputElement)) return;
        if (!el.classList.contains("bulk-dev-pick")) return;
        const did = String(el.dataset.deviceId || "").trim();
        if (!did) return;
        if (el.checked) selectedIds.add(did);
        else selectedIds.delete(did);
        bulkBarState();
      });
    }
    view.addEventListener("click", onFwHintClick);
  };
  const wireBulkToolbar = () => {
    if (bulkWired) return;
    if (!$("#grpAllDevFilter", view)) return;
    bulkWired = true;
    const f = $("#grpAllDevFilter", view);
    const scheduleSearchReload = debounceDevicesSearch((q) => {
      if (!isRouteCurrent(routeSeq)) return;
      const sq = String(q || "").trim();
      if (sq === activeServerSearchQ && allItems.length && devicesSearchQueryReady(sq)) {
        render();
        return;
      }
      loadDevicesAndHints({ searchQ: sq, append: false });
    });
    if (f) {
      f.addEventListener("input", () => {
        const raw = String(f.value || "").trim();
        syncDevicesSearchMinLenBanner(view, devicesSearchTooShort(raw));
        if (devicesSearchQueryReady(raw)) {
          scheduleSearchReload(f.value);
        } else if (!raw) {
          if (activeServerSearchQ) loadDevicesAndHints({ searchQ: "", append: false });
          else {
            syncDevicesServerSearchBanner(view, false);
            render();
          }
        } else if (activeServerSearchQ) {
          loadDevicesAndHints({ searchQ: "", append: false });
        } else {
          render();
        }
      });
    }
    const selVis = $("#grpBulkSelectVisible", view);
    if (selVis) {
      selVis.addEventListener("click", () => {
        for (const d of filteredItems()) {
          const did = String(d.device_id || "").trim();
          if (did) selectedIds.add(did);
        }
        render();
      });
    }
    const clrSel = $("#grpBulkClearSel", view);
    if (clrSel) {
      clrSel.addEventListener("click", () => {
        selectedIds.clear();
        render();
      });
    }
    const grpBtn = $("#grpBulkApplyGroup", view);
    if (grpBtn) {
      grpBtn.addEventListener("click", async () => {
        const rawG = String($("#grpBulkGroupValue", view)?.value || "").trim();
        const grpVal = rawG ? canonicalGroupKey(rawG) : "";
        const promptTxt = grpVal
          ? `Apply group "${grpVal}" to Selected boards (${selectedIds.size})?`
          : `Clear group for Selected boards (${selectedIds.size})?`;
        if (!confirm(promptTxt)) return;
        try {
          await runBulkProfile({ set_notification_group: true, notification_group: grpVal });
        } catch (e) { toast(e.message || e, "err"); }
      });
    }
    const zoneBtn = $("#grpBulkApplyZone", view);
    if (zoneBtn) {
      zoneBtn.addEventListener("click", async () => {
        const z = String($("#grpBulkZoneValue", view)?.value || "").trim();
        if (!z) { toast("Enter zone value", "err"); return; }
        if (!confirm(`Apply zone override "${z}" to Selected boards (${selectedIds.size})?`)) return;
        try {
          await runBulkProfile({ set_zone_override: true, zone_override: z });
        } catch (e) { toast(e.message || e, "err"); }
      });
    }
    const clrZoneBtn = $("#grpBulkClearZone", view);
    if (clrZoneBtn) {
      clrZoneBtn.addEventListener("click", async () => {
        if (!confirm(`Clear zone override for Selected boards (${selectedIds.size})?`)) return;
        try {
          await runBulkProfile({ clear_zone_override: true });
        } catch (e) { toast(e.message || e, "err"); }
      });
    }
  };

  const wire = () => {
    wireGrid();
    wireBulkToolbar();
  };

  const onFwHintClick = (ev) => {
    const t = ev.target && ev.target.closest && ev.target.closest(".js-fw-hint");
    if (!t || !view.contains(t)) return;
    ev.preventDefault();
    ev.stopPropagation();
    const did0 = t.getAttribute("data-did");
    const h = did0 && hintById.get(did0);
    const row = did0 ? allItems.find((x) => String(x.device_id) === String(did0)) : null;
    if (h) {
      openGlobalFwHintDialog(h, {
        currentFw: row && row.fw != null ? String(row.fw) : "",
        deviceId: String(did0 || ""),
        canOperateThisDevice: undefined,
      });
    }
  };

  const dispose = () => {
    view.removeEventListener("click", onFwHintClick);
    if (typeof deviceGridVirtualDispose === "function") {
      try { deviceGridVirtualDispose(); } catch (_) {}
      deviceGridVirtualDispose = null;
    }
    gridWired = false;
    bulkWired = false;
  };

  return {
    getRows,
    getSearchQ,
    getFleetCursor,
    getFleetHasMore,
    hydrate,
    render,
    wire,
    wireGrid,
    wireBulkToolbar,
    dispose,
    reload: loadDevicesAndHints,
    loadFirmwareHints,
  };
}
